import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import Loader from 'components/Loader/Loader';

const AlertDetailsModal = ({ open, alertId, onClose }) => {
  const [alertDetails, setAlertDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEvidence, setExpandedEvidence] = useState(null);
  const [kbDraft, setKbDraft] = useState(null);
  const [investigationError, setInvestigationError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [kbActionLoading, setKbActionLoading] = useState(false);
  const [kbActionError, setKbActionError] = useState('');
  const [kbJustApproved, setKbJustApproved] = useState(false);
  const { accessToken } = useContext(AuthContext);

  const startInvestigation = async (id) => {
    try {
      const response = await fetch(UrlHelpers.autopsInvestigateAlert(encodeURIComponent(id)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          appid: 'notifyadmin',
        },
        body: JSON.stringify({
          logExcerpt: '',
        }),
      });

      return response.status;
    } catch (error) {
      return 403;
    }
  };

  const getAlertDetails = async (id) => {
    const response = await fetch(UrlHelpers.autopsAlertDetails(id), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        appid: 'notifyadmin',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch alert details');
    }

    return response.json();
  };

  const getKbDraft = async (alarmName) => {
    const response = await fetch(UrlHelpers.autopsKbDraft(encodeURIComponent(alarmName)), {
      headers: { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' },
    });

    if (response.status === 204 || response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to fetch KB draft');
    }

    return response.json();
  };

  const approveKbDraft = async (alarmName) => {
    const response = await fetch(UrlHelpers.autopsKbDraftApprove(encodeURIComponent(alarmName)), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' },
    });

    if (!response.ok) {
      throw new Error(`Approve failed: ${response.status}`);
    }

    return response.json();
  };

  const rejectKbDraft = async (alarmName) => {
    const response = await fetch(UrlHelpers.autopsKbDraftReject(encodeURIComponent(alarmName)), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' },
    });

    if (!response.ok) {
      throw new Error(`Reject failed: ${response.status}`);
    }

    return response.json();
  };

  const handleKbApprove = async () => {
    try {
      setKbActionLoading(true);
      setKbActionError('');

      const updated = await approveKbDraft(alertDetails.alarmName);

      setKbDraft(updated);
      setKbJustApproved(updated?.status === 'Approved');
    } catch (error) {
      setKbActionError(error instanceof Error ? error.message : 'Approve failed.');
    } finally {
      setKbActionLoading(false);
    }
  };

  const handleKbReject = async () => {
    try {
      setKbActionLoading(true);
      setKbActionError('');

      const updated = await rejectKbDraft(alertDetails.alarmName);

      setKbDraft(updated);
    } catch (error) {
      setKbActionError(error instanceof Error ? error.message : 'Reject failed.');
    } finally {
      setKbActionLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !alertId) return;
    setInvestigationError('');
    const fetchAlertDetails = async () => {
      try {
        setLoading(true);

        const data = await getAlertDetails(alertId);

        if (data.lifecycleState === 'Pending') {
          const status = await startInvestigation(alertId);

          if (status === 403) {
            setInvestigationError('Investigation could not be started: 403');
            return;
          }
        }

        setAlertDetails(data);
      } catch (error) {
        console.error('Alert Details Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlertDetails();
  }, [open, alertId]);

  useEffect(() => {
    if (!alertDetails?.rcaResult) {
      return;
    }

    const fetchKbDraft = async () => {
      try {
        setKbJustApproved(false);
        const data = await getKbDraft(alertDetails.alarmName);
        setKbDraft(data);
      } catch (error) {
        setKbDraft(null);
      }
    };

    fetchKbDraft();
  }, [alertDetails]);

  const formatActivityDateTime = (value) => {
    if (!value) return '-';

    const normalized =
      typeof value === 'string' ? value.replace(/(\d{2}:\d{2}:\d{2}):(\d+)/, '$1.$2') : value;

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const reviewAlert = async (id) => {
    const response = await fetch(UrlHelpers.autopsReviewAlert(encodeURIComponent(id)), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' },
    });

    if (!response.ok) {
      throw new Error(`Review failed: ${response.status}`);
    }

    return response.json();
  };

  const rejectAlert = async (id) => {
    const response = await fetch(UrlHelpers.autopsRejectAlert(encodeURIComponent(id)), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, appid: 'notifyadmin' },
    });

    if (!response.ok) {
      throw new Error(`Reject failed: ${response.status}`);
    }

    return response.json();
  };

  const handleReview = async () => {
    try {
      setActionLoading(true);
      setActionError('');

      const updatedAlert = await reviewAlert(alertId);

      setAlertDetails(updatedAlert);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Review failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      setActionError('');

      const updatedAlert = await rejectAlert(alertId);

      setAlertDetails(updatedAlert);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Reject failed.');
    } finally {
      setActionLoading(false);
    }
  };
  //download report
  const downloadReport = () => {
    const rca = alertDetails?.rcaResult;

    const evidenceLines = (rca?.evidence || []).map((e) =>
      e.type === 'kb'
        ? `- KB — ${e.source_uri} (score ${e.score ?? 'n/a'}): ${e.excerpt}`
        : `- Log — ${e.source}: ${e.excerpt}`
    );

    const remediationLines = (rca?.remediation || []).map((step, i) => `${i + 1}. ${step}`);

    const markdown = [
      `# Investigation report — ${alertDetails.alarmName}`,
      '',
      `**Alarm ID:** ${rca?.alarmId || ''}`,
      `**Application:** ${alertDetails.application}`,
      `**Tier:** ${rca?.tier || ''}`,
      `**Confidence:** ${rca?.confidence || ''} (informational only)`,
      `**Tools used:** ${rca?.tools_used?.join(', ') || 'none'}`,
      ...(rca?.handoff ? [`**Handoff:** ${rca?.handoff_reason || ''}`] : []),
      '',
      '## Root cause',
      rca?.root_cause || '',
      '',
      '## Supporting evidence',
      ...evidenceLines,
      '',
      '## Prescribed remediation',
      ...remediationLines,
    ].join('\n');

    const blob = new Blob([markdown], {
      type: 'text/markdown',
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${alertDetails.alertId}-report.md`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  const KB_STATUS_COPY = {
    'Approved-PendingIngest': 'Approved — indexing in progress',
    Approved: 'Approved',
    'Approved-IngestFailed': 'Approved — indexing failed',
    Rejected: 'Rejected',
  };

  const kbIsPending = kbDraft?.status === 'Pending';
  const kbDisplayStatus =
    kbJustApproved && kbDraft?.status === 'Approved' ? 'Approved-PendingIngest' : kbDraft?.status;

  const SUGGESTION_ROWS = [
    {
      key: 'suggested_script',
      label: 'Suggested Script',
      caption: 'Not executed — for human review only.',
    },
    {
      key: 'suggested_monitoring_rule',
      label: 'Suggested Monitoring Rule',
      caption: 'Not applied — for human review only.',
    },
    {
      key: 'suggested_notification',
      label: 'Suggested Notification',
      caption: 'Not sent — for human review only.',
    },
  ];

  const renderHealthCheck = () => {
    const hc = alertDetails?.healthCheck;

    if (!hc) return null;

    let text;

    if (hc.no_data) {
      text = 'No confirmed alarm coverage';
    } else if (hc.total_count === 0) {
      text = 'No related CloudWatch alarms';
    } else {
      text = `${hc.total_count - hc.degraded_count} of ${hc.total_count} alarms OK`;
    }

    if (hc.lambda_state) {
      text += ` • Lambda: ${hc.lambda_state}`;
    }

    return text;
  };

  const renderMetrics = () => {
    const m = alertDetails?.metrics;

    if (!m) return null;

    if (m.no_data || m.error_rate_pct === null || m.error_rate_pct === undefined) {
      return 'No metrics data available';
    }

    let text = `Error rate: ${m.error_rate_pct}%`;

    if (m.baseline_error_rate_pct !== null && m.baseline_error_rate_pct !== undefined) {
      text += ` (baseline ${m.baseline_error_rate_pct}%)`;
    }

    return text;
  };

  const isTerminal =
    alertDetails?.lifecycleState === 'Reviewed' || alertDetails?.lifecycleState === 'Rejected';

  const getStatusChipStyle = (status) => {
    const value = status?.toLowerCase();

    switch (value) {
      case 'done':
        return {
          backgroundColor: '#22c55e1f',
          color: '#16a34a',
        };

      case 'handoff':
        return {
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
        };

      case 'awaiting review':
        return {
          backgroundColor: '#ede9fe',
          color: '#7c3aed',
        };

      case 'not validated':
        return {
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
        };

      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#616161',
        };
    }
  };
  if (investigationError) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Loader open={actionLoading} />
        <DialogContent sx={{ p: 3 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography>{investigationError}</Typography>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Paper>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        ) : (
          alertDetails && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography
                    sx={{ color: '#6b7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    ALERT ID
                  </Typography>

                  <Typography
                    sx={{
                      color: '#111827',
                      fontWeight: 700,
                      fontSize: '15px !important',
                      marginTop: '10px',
                    }}
                  >
                    {alertDetails.alertId}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    sx={{
                      border: '1px solid #d1d5db',
                      background: '#f8fafc',
                      color: '#6b7280',
                      padding: '8px 10px',
                      borderRadius: '30px',
                    }}
                  >
                    {alertDetails.errorType || 'TECHNICAL'}
                  </Typography>

                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={3}>
                <Box>
                  <Typography
                    sx={{ color: '#6B7280', fontSize: '12px !important', fontWeight: 600 }}
                  >
                    SEVERITY
                  </Typography>

                  <Chip
                    label={alertDetails.criticality}
                    size="small"
                    sx={{
                      mt: 1,
                      backgroundColor: '#ffebee',
                      color: '#d32f2f',
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{ color: '#6B7280', fontSize: '12px !important', fontWeight: 600 }}
                  >
                    SOURCE
                  </Typography>

                  <Typography
                    sx={{ fontSize: '15px !important', color: '#111827', marginTop: '8px' }}
                  >
                    {alertDetails.application}
                  </Typography>
                </Box>
              </Box>

              <Box mb={3}>
                <Typography sx={{ color: '#6B7280', fontSize: '12px !important', fontWeight: 600 }}>
                  TIMESTAMP
                </Typography>

                <Typography
                  sx={{ fontSize: '15px !important', color: '#111827', marginTop: '8px' }}
                >
                  {formatActivityDateTime(alertDetails.activityDatetime)}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography sx={{ color: '#6B7280', fontSize: '12px !important', fontWeight: 600 }}>
                  TITLE
                </Typography>

                <Typography
                  sx={{
                    color: '#08060d',
                    fontSize: '22px !important',
                    fontWeight: 600,
                    marginTop: '8px',
                  }}
                >
                  {alertDetails.alarmName}
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: '10px',
                  padding: '14px 18px',
                  border: '1px solid #16a34a',
                  backgroundColor: '#22c55e1f',
                }}
              >
                <Typography
                  sx={{
                    color: '#16a34a',
                    fontWeight: 700,
                    fontSize: '13px !important',
                    mb: '10px',
                  }}
                >
                  Generated Artifacts
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                  }}
                >
                  <Typography
                    sx={{ color: '#6B7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    Incident Report:
                  </Typography>
                  <Typography
                    sx={{ color: '#111827', fontSize: '14px !important', fontWeight: 600 }}
                  >
                    Available
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    mt: '8px',
                  }}
                >
                  <Typography
                    sx={{ color: '#6B7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    System Health Check:
                  </Typography>
                  <Typography
                    sx={{ color: '#111827', fontSize: '14px !important', fontWeight: 600 }}
                  >
                    {renderHealthCheck() || 'Not available'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    mt: '8px',
                  }}
                >
                  <Typography
                    sx={{ color: '#6B7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    Metrics Snapshot:
                  </Typography>
                  <Typography
                    sx={{ color: '#111827', fontSize: '14px !important', fontWeight: 600 }}
                  >
                    {renderMetrics() || 'Not available'}
                  </Typography>
                </Box>

                {SUGGESTION_ROWS.map(({ key, label, caption }) => {
                  const text = alertDetails.rcaResult?.[key];

                  if (!text) return null;

                  return (
                    <Box
                      key={key}
                      sx={{
                        mt: '12px',
                        pt: '12px',
                        borderTop: '1px dashed #86efac',
                      }}
                    >
                      <Typography
                        sx={{ color: '#6B7280', fontWeight: 600, fontSize: '14px !important' }}
                      >
                        {label}:
                      </Typography>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px !important', mt: '2px' }}>
                        {caption}
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          color: '#111827',
                          fontSize: '13px !important',
                          mt: '6px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'inherit',
                          margin: '6px 0 0 0',
                        }}
                      >
                        {text}
                      </Box>
                    </Box>
                  );
                })}

                {kbDraft && (
                  <Box
                    sx={{
                      mt: '12px',
                      pt: '12px',
                      borderTop: '1px dashed #86efac',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                      }}
                    >
                      <Typography
                        sx={{ color: '#6B7280', fontWeight: 600, fontSize: '14px !important' }}
                      >
                        Knowledge Base Entry:
                      </Typography>
                      <Typography
                        sx={{ color: '#111827', fontSize: '14px !important', fontWeight: 600 }}
                      >
                        {kbIsPending
                          ? 'Pending Review'
                          : KB_STATUS_COPY[kbDisplayStatus] || kbDisplayStatus}
                      </Typography>
                    </Box>

                    {kbDraft.sourceAlertId && (
                      <Typography sx={{ color: '#6b7280', fontSize: '12px !important', mt: '4px' }}>
                        {kbDraft.sourceAlertId === alertDetails.alertId
                          ? 'Drafted from this alert.'
                          : `Drafted from another alert for this alarm: ${kbDraft.sourceAlertId}`}
                      </Typography>
                    )}

                    {kbIsPending && (
                      <Box sx={{ mt: '10px' }}>
                        <Typography
                          sx={{ color: '#111827', fontSize: '13px !important', mb: '10px' }}
                        >
                          {kbDraft.narrative}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '13px !important',
                            mb: '6px',
                          }}
                        >
                          Source evidence
                        </Typography>

                        {kbDraft.sourceEvidence?.rootCause && (
                          <Typography
                            sx={{ color: '#6b6375', fontSize: '13px !important', mb: '8px' }}
                          >
                            {kbDraft.sourceEvidence.rootCause}
                          </Typography>
                        )}

                        {kbDraft.sourceEvidence?.remediation?.length > 0 && (
                          <Box component="ol" sx={{ pl: 3, mt: 0, mb: '10px' }}>
                            {kbDraft.sourceEvidence.remediation.map((step, i) => (
                              <Box
                                component="li"
                                key={i}
                                sx={{ color: '#6b6375', fontSize: '13px !important', mb: '4px' }}
                              >
                                {step}
                              </Box>
                            ))}
                          </Box>
                        )}

                        {kbActionError && (
                          <Typography
                            sx={{ color: '#dc2626', fontSize: '13px !important', mb: '10px' }}
                          >
                            {kbActionError}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={kbActionLoading}
                            onClick={handleKbReject}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={kbActionLoading}
                            onClick={handleKbApprove}
                          >
                            Approve
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>

              <Box
                sx={{
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  mt: '30px',
                }}
              >
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: '13px !important',
                    fontWeight: 700,
                  }}
                >
                  AI PROCESSING TIMELINE
                </Typography>
                <hr style={{ margin: '15px 0px' }} />

                {alertDetails.timeline?.map((item) => (
                  <Paper
                    key={item.step}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        sx={{ fontWeight: 600, fontSize: '14px !important', color: '#6b6375' }}
                      >
                        {item.step}. {item.title}
                      </Typography>

                      <Chip
                        size="small"
                        label={item.status.toUpperCase()}
                        sx={{
                          ...getStatusChipStyle(item.status),
                          fontWeight: 600,
                          fontSize: '12px !important',
                          padding: '2px 10px',
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '13px !important',
                        color: '#6b7280',
                        margin: '10px 0px 0px 25px',
                      }}
                    >
                      {item.detail}
                    </Typography>
                  </Paper>
                ))}

                <Paper
                  elevation={0}
                  sx={{
                    minHeight: 80,
                    p: 2,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Typography
                    sx={{ color: '#6b7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    SUPPORTING EVIDENCE
                  </Typography>
                  <hr style={{ margin: '15px 0px' }} />

                  {alertDetails.rcaResult?.evidence?.length ? (
                    alertDetails.rcaResult.evidence.map((evidence, index) => {
                      const expanded = expandedEvidence === index;

                      return (
                        <Paper
                          key={evidence.score}
                          elevation={0}
                          sx={{
                            mb: 2,
                            border: '1px solid #e5e7eb',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                            onClick={() => setExpandedEvidence(expanded ? null : index)}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                <strong>{evidence.type === 'kb' ? 'KB' : 'Log'}</strong>
                                {' — '}
                                {evidence.source_uri || evidence.source}

                                {evidence.score && (
                                  <span
                                    style={{
                                      color: '#14b8a6',
                                      marginLeft: 12,
                                      fontWeight: 600,
                                    }}
                                  >
                                    score {evidence.score.toFixed(2)}
                                  </span>
                                )}
                              </Typography>
                            </Box>

                            {expanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </Box>

                          {expanded && (
                            <Box
                              sx={{
                                px: 2,
                                pb: 2,
                                borderLeft: '3px solid #14b8a6',
                                ml: 2,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {evidence.excerpt}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      );
                    })
                  ) : (
                    <Typography
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                        py: 3,
                      }}
                    >
                      No supporting evidence available.
                    </Typography>
                  )}
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid #e5e7eb',
                    mt: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{ color: '#6b7280', fontWeight: 600, fontSize: '14px !important' }}
                    >
                      RECOMMENDED ACTIONS
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        alertDetails.lifecycleState === 'Reviewed'
                          ? 'Reviewed'
                          : alertDetails.lifecycleState === 'Rejected'
                          ? 'Rejected'
                          : 'Awaiting Review'
                      }
                      color={
                        alertDetails.lifecycleState === 'Reviewed'
                          ? 'success'
                          : alertDetails.lifecycleState === 'Rejected'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </div>
                  <hr style={{ margin: '15px 0px' }} />
                  <Typography sx={{ color: '#9ca3af', mb: '10px' }}>
                    Recommended remediation only — nothing on this page executes a script or
                    contacts an external system. Reject/Mark Reviewed record a human decision here;
                    they take no action against {alertDetails.application}.
                  </Typography>
                  {alertDetails.rcaResult?.remediation?.map((item, index) => (
                    <Typography
                      key={index}
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px !important',
                        color: '#6b6375',
                        mb: '10px',
                      }}
                    >
                      {index + 1}. {item}
                    </Typography>
                  ))}

                  <div>
                    {actionError && (
                      <Typography
                        sx={{
                          color: '#dc2626',
                          fontSize: '13px !important',
                          mb: 2,
                        }}
                      >
                        {actionError}
                      </Typography>
                    )}
                  </div>
                  <Box display="flex" gap={1} mt={3} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={isTerminal || actionLoading}
                      onClick={handleReject}
                    >
                      Reject
                    </Button>

                    <Typography style={{ fontSize: '17px !important', padding: '12px' }}>
                      RCA Generated
                    </Typography>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleReview}
                      disabled={isTerminal || actionLoading}
                    >
                      Mark Reviewed
                    </Button>

                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        backgroundColor: '#15b8a6',
                      }}
                      onClick={downloadReport}
                    >
                      Download Report
                    </Button>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    my: '20px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Typography
                    sx={{ color: '#6b7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    INVESTIGATION SUMMARY
                  </Typography>
                  <hr style={{ margin: '15px 0px' }} />
                  {alertDetails.rcaResult?.handoff && (
                    <Box
                      sx={{
                        padding: '10px 14px',
                        backgroundColor: '#f59e0b1f',
                        border: '1px solid #d97706',
                        borderRadius: '10px',
                        mb: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#d97706',
                          fontSize: '18px !important',
                          fontWeight: 700,
                        }}
                      >
                        Escalation Required
                      </Typography>

                      <Typography sx={{ color: '#6b7280', fontSize: '13px !important', mt: '4px' }}>
                        {alertDetails.rcaResult?.handoff_reason}
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    sx={{ color: '#6b7280', fontWeight: 600, fontSize: '14px !important' }}
                  >
                    ROOT CAUSE
                  </Typography>

                  <Typography sx={{ fontSize: '14px !important', color: '#6b6375', my: '10px' }}>
                    {alertDetails.rcaResult?.root_cause}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                    <Typography sx={{ fontSize: '13px !important', color: '#6b6375' }}>
                      Confidence: <strong>{alertDetails.rcaResult?.confidence}</strong>
                    </Typography>

                    <Typography sx={{ color: '#9ca3af' }}>
                      Tier: {alertDetails.rcaResult?.tier} Tools:{' '}
                      {alertDetails.rcaResult?.tools_used?.join(', ')}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AlertDetailsModal;

AlertDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  alertId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

AlertDetailsModal.defaultProps = {
  alertId: null,
};
